import { ValidationResultReport, BankValidation, DocumentClassValidation } from '../api/apiTypes';

/**
 * Vocabolario completo delle label restituite da POST /reports/best_execution/validate.
 *
 * Il valore non e' un enum nell'OpenAPI (sono stringhe dentro array), quindi questa
 * tabella e' l'unica fonte di verita' lato frontend. `blocks` significa: l'entita' e'
 * NOT OK e la creazione del report verrebbe rifiutata con 409 per lo stesso motivo.
 */
export type ValidationLabel =
  // errors
  | 'missing_source'
  | 'bank_not_found'
  // actions
  | 'indexation_needed'
  | 'old_data'
  | 'source_changed'
  // info
  | 'indexing_in_progress'
  | 'check_in_progress'
  | 'version_check_in_progress'
  | 'source_new_version'
  | 'source_unreachable'
  | 'latest_ingestion_failed'
  | 'reingestion_in_progress';

export type ValidationList = 'errors' | 'actions' | 'info';

export interface ValidationLabelSpec {
  /** In quale delle tre liste arriva. */
  list: ValidationList;
  /** Se presente, l'entita' e' NOT OK e la creazione verrebbe rifiutata. */
  blocks: boolean;
  /** Testo del badge. */
  label: string;
  /** Cosa deve fare l'utente, quando c'e' qualcosa da fare. */
  action?: string;
  /** Finche' e' presente va ripetuto /validate: qualcosa sta girando lato server. */
  polling?: boolean;
}

export const VALIDATION_LABELS: Record<ValidationLabel, ValidationLabelSpec> = {
  // --- errors -------------------------------------------------------------
  missing_source: {
    list: 'errors',
    blocks: true,
    label: 'Fonte mancante',
    action: 'Registra una fonte',
  },
  bank_not_found: {
    list: 'errors',
    blocks: true,
    label: 'Banca non trovata',
    action: 'Errore di configurazione, contatta il supporto',
  },

  // --- actions ------------------------------------------------------------
  indexation_needed: {
    list: 'actions',
    blocks: true,
    label: 'Indicizzazione necessaria',
    action: 'Indicizza',
  },
  old_data: {
    list: 'actions',
    blocks: true,
    label: 'Dati obsoleti',
    action: "Registra un documento piu' recente",
  },
  source_changed: {
    list: 'actions',
    blocks: true,
    label: "Fonte cambiata all'origine",
    action: 'Scegli se tenere la versione attuale o passare alla nuova',
  },

  // --- info ---------------------------------------------------------------
  indexing_in_progress: {
    list: 'info',
    blocks: true,
    label: 'Indicizzazione in corso',
    polling: true,
  },
  check_in_progress: {
    list: 'info',
    blocks: true,
    label: 'Controllo freschezza in corso',
    polling: true,
  },
  version_check_in_progress: {
    list: 'info',
    blocks: false,
    label: 'Controllo versione in corso',
    polling: true,
  },
  source_new_version: {
    list: 'info',
    blocks: false,
    label: 'Nuova versione disponibile',
    action: 'Solo informativo: le pagine HTML cambiano a ogni fetch',
  },
  source_unreachable: {
    list: 'info',
    blocks: false,
    label: 'Origine non raggiungibile',
    action: 'I bytes registrati restano quelli indicizzati',
  },
  latest_ingestion_failed: {
    list: 'info',
    blocks: false,
    label: 'Ultima indicizzazione fallita',
    action: 'Una indicizzazione precedente copre ancora la classe: puoi riprovare con "Riprocessa tutto"',
  },
  reingestion_in_progress: {
    list: 'info',
    blocks: false,
    label: 'Reindicizzazione in corso',
    polling: true,
  },
};

/** Label legacy non piu' nel vocabolario del BE, tenuta per non regredire la UI esistente. */
const LEGACY_LABELS: Record<string, ValidationLabelSpec> = {
  error: { list: 'info', blocks: true, label: 'Errore indicizzazione' },
};

export const getLabelSpec = (value: string): ValidationLabelSpec | undefined =>
  VALIDATION_LABELS[value as ValidationLabel] ?? LEGACY_LABELS[value];

/** Non lancia mai su una label sconosciuta: la mostra cosi' com'e'. */
export const translateLabel = (value: string): string => getLabelSpec(value)?.label ?? value;

export const isBlockingLabel = (value: string): boolean => getLabelSpec(value)?.blocks ?? false;

export const isPollingLabel = (value: string): boolean => getLabelSpec(value)?.polling ?? false;

// ---------------------------------------------------------------------------
// Aggregazioni sul risultato di /validate
// ---------------------------------------------------------------------------

const emptyDoc: DocumentClassValidation = { errors: [], actions: [], info: [] };

/** Tutte le entita' (banche + general) con il loro nome. */
export const allEntities = (r: ValidationResultReport): Array<[string, BankValidation]> => [
  ...Object.entries(r.banks ?? {}),
  ...(r.general ? ([['general', r.general]] as Array<[string, BankValidation]>) : []),
];

/** Tutte le classi documento, con banca e tipo, appiattite. */
export const allDocumentClasses = (
  r: ValidationResultReport,
): Array<{ entity: string; documentType: string; validation: DocumentClassValidation }> =>
  allEntities(r).flatMap(([entity, bank]) =>
    Object.entries(bank?.document_classes ?? {}).map(([documentType, validation]) => ({
      entity,
      documentType,
      validation: { ...emptyDoc, ...validation },
    })),
  );

/**
 * La validazione di una singola classe documento, cercata fra tutte le entita'.
 *
 * Un `document_type` compare sotto una sola entita': le classi generali sotto
 * `general`, quelle bancarie sotto il nome della banca. Cercare ovunque evita di
 * dover conoscere quel nome, che /validate restituisce ma il chiamante non ha.
 */
export const classValidation = (
  r: ValidationResultReport | null,
  documentType: string,
): DocumentClassValidation | null => {
  if (!r) return null;
  const trovata = allDocumentClasses(r).find((c) => c.documentType === documentType);
  return trovata ? trovata.validation : null;
};

/** Le label di una classe, nell'ordine in cui vanno mostrate: prima ciò che blocca. */
export const classLabels = (v: DocumentClassValidation | null): string[] =>
  v ? [...v.errors, ...v.actions, ...v.info] : [];

/** Tutte le label presenti, senza distinzione di lista. */
export const allLabels = (r: ValidationResultReport): string[] =>
  allDocumentClasses(r).flatMap(({ validation }) => [
    ...validation.errors,
    ...validation.actions,
    ...validation.info,
  ]);

export const hasLabel = (r: ValidationResultReport, label: string): boolean =>
  allLabels(r).includes(label);

/** Classi che portano una certa label, per costruire messaggi puntuali. */
export const classesWithLabel = (r: ValidationResultReport, label: string) =>
  allDocumentClasses(r).filter(
    ({ validation }) =>
      validation.errors.includes(label) ||
      validation.actions.includes(label) ||
      validation.info.includes(label),
  );

/** Se true, qualcosa sta girando lato server: ripeti /validate fra qualche secondo. */
export const shouldKeepPolling = (r: ValidationResultReport): boolean =>
  allLabels(r).some(isPollingLabel);

/** Label bloccanti presenti, deduplicate e in ordine di comparsa. */
export const blockingLabels = (r: ValidationResultReport): string[] =>
  Array.from(new Set(allLabels(r).filter(isBlockingLabel)));

/**
 * Il report si puo' creare solo se il BE dice OK.
 * Volutamente NON si deduce da "nessuna label che conosco": una label nuova
 * lato BE non deve trasformarsi in un via libera.
 */
export const canCreateReport = (r: ValidationResultReport | null): boolean => {
  if (!r) return false;
  if (r.status !== 'OK') return false;
  return allEntities(r).every(([, bank]) => bank?.status === 'OK');
};

/** Label che il frontend non conosce: utile in console per accorgersi di una release nuova. */
export const unknownLabels = (r: ValidationResultReport): string[] =>
  Array.from(new Set(allLabels(r).filter((l) => !getLabelSpec(l))));
